import React, { useEffect, useMemo, useState } from 'react';
import { Header, Label, Message, Table } from 'semantic-ui-react';
import Loader from '../../../../../components/loader';
import TableUpdatesRepo from '../../../../../utils/repository/tableUpdatesRepo';
import UserRepo from '../../../../../utils/repository/userRepo';
import { ROLES } from '../../../../../constnats/user';
import { TableUpdateInfoType } from '../../../../../schemas/tableUpdate.schemas';
import { UserType } from '../../../../../types/user.type';
import {
    DEFAULT_MAX_ACTIVE_REVIEW_UPDATES,
    REVIEW_PHASES,
    countActiveReconciliationsByOwner,
    getPromotionAllowanceLimit,
    isAllowanceExempt,
} from '../../../../../utils/promotionAllowance';
import './promotionAllowance.css';

type AllowanceRow = {
    employee: UserType;
    limit: number;
    allowance: number;
};

function fetchReviewUpdates(): Promise<TableUpdateInfoType[]> {
    return new Promise((resolve) => {
        TableUpdatesRepo.findAll({ phases: REVIEW_PHASES }, resolve);
    });
}

function fetchEmployees(): Promise<UserType[]> {
    return new Promise((resolve) => {
        UserRepo.list({ userRoles: [ROLES.EMPLOYEE], includeDisabled: false }, resolve);
    });
}

function PromotionAllowance() {
    const [loading, setLoading] = useState(true);
    const [updates, setUpdates] = useState<TableUpdateInfoType[]>([]);
    const [employees, setEmployees] = useState<UserType[]>([]);

    useEffect(() => {
        let active = true;
        Promise.all([fetchReviewUpdates(), fetchEmployees()]).then(([reviewUpdates, allEmployees]) => {
            if (!active) {
                return;
            }
            setUpdates(reviewUpdates);
            setEmployees(allEmployees);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, []);

    const rows: AllowanceRow[] = useMemo(() => {
        const countByOwner = countActiveReconciliationsByOwner(updates);

        return employees
            .filter((employee) => !isAllowanceExempt(employee.fullName))
            .map((employee) => {
                const limit = getPromotionAllowanceLimit(employee.id);
                return {
                    employee,
                    limit,
                    allowance: limit - (countByOwner.get(employee.id) ?? 0),
                };
            })
            .filter((row) => row.allowance > 0)
            .sort(
                (a, b) =>
                    b.allowance - a.allowance || a.employee.fullName.localeCompare(b.employee.fullName)
            );
    }, [updates, employees]);

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="promotionAllowance">
            <Header as="h3">Reconciliation Draft Promotion Allowance</Header>
            <p className="promotionAllowance__hint">
                Each active employee may have up to their allowance (default {DEFAULT_MAX_ACTIVE_REVIEW_UPDATES})
                {' '}of reconciliation updates in Peer or QA review. The Can Promote column shows how many
                more reconciliation drafts they may move to Ready for Peer Review. Deferred review
                execution updates are exempt from this limit and do not consume allowance. Updates on
                other schemas are not restricted.
            </p>
            {rows.length === 0 ? (
                <Message info content="No employees currently have remaining promotion allowance." />
            ) : (
                <Table striped celled compact collapsing>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell collapsing>#</Table.HeaderCell>
                            <Table.HeaderCell>Employee</Table.HeaderCell>
                            <Table.HeaderCell collapsing textAlign="center">Allowance</Table.HeaderCell>
                            <Table.HeaderCell collapsing textAlign="center">Can Promote</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rows.map((row, index) => (
                            <Table.Row key={row.employee.id}>
                                <Table.Cell collapsing>{index + 1}</Table.Cell>
                                <Table.Cell>{row.employee.fullName}</Table.Cell>
                                <Table.Cell textAlign="center">{row.limit}</Table.Cell>
                                <Table.Cell textAlign="center">
                                    <Label color="green" size="small">{row.allowance}</Label>
                                </Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            )}
        </div>
    );
}

export default PromotionAllowance;
